import React, { useState, useRef, useEffect } from 'react';
import { motion, useMotionValue, useTransform, useAnimation, PanInfo } from 'framer-motion';
import { Share, Trash2 } from 'lucide-react';

interface SwipeableTourCardProps {
  children: React.ReactNode;
  onShare: () => void;
  onDelete?: () => void;
  onClick: () => void;
  className?: string;
  style?: React.CSSProperties;
}

const SwipeableTourCard: React.FC<SwipeableTourCardProps> = ({
  children,
  onShare,
  onDelete,
  onClick,
  className,
  style
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const isDragging = useRef(false);
  const hasMoved = useRef(false);
  const startX = useRef(0);
  const controls = useAnimation();
  const x = useMotionValue(0);
  const cardRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Thresholds for swipe
  const SWIPE_THRESHOLD = -50;
  const OPEN_POSITION = onDelete ? -140 : -70; // Width based on number of buttons (70px each)
  const MOVEMENT_THRESHOLD = 3; // pixels before we consider it a swipe not a tap

  // Opacity for the action buttons container
  // Only visible when swiping left (x < 0)
  const actionsOpacity = useTransform(x, [-10, 0, 10], [1, 0, 0]);

  // Close card when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isOpen && cardRef.current && !cardRef.current.contains(event.target as Node)) {
        controls.start({ x: 0 });
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside as any);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside as any);
    };
  }, [isOpen, controls]);

  const handleDragStart = () => {
    isDragging.current = true;
    hasMoved.current = true;
  };

  const handleDragEnd = async (event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    // Small delay to prevent immediate click after drag
    setTimeout(() => {
      isDragging.current = false;
      hasMoved.current = false;
    }, 100);

    const offset = info.offset.x;
    const velocity = info.velocity.x;

    if (offset < SWIPE_THRESHOLD || velocity < -500) {
      // Swipe left - open
      await controls.start({ x: OPEN_POSITION });
      setIsOpen(true);
    } else {
      // Snap back
      await controls.start({ x: 0 });
      setIsOpen(false);
    }
  };

  const handleCardClick = () => {
    if (isDragging.current || hasMoved.current) return;
    
    if (isOpen) {
      // If open, close it
      controls.start({ x: 0 });
      setIsOpen(false);
    } else {
      // Navigate
      onClick();
    }
  };

  return (
    <div 
      ref={cardRef}
      style={{ 
        position: 'relative', 
        overflow: 'hidden',
        borderRadius: '16px',
        display: 'block',
        ...style 
      }} 
      className={className}
    >
      {/* Action Buttons Background */}
      <motion.div 
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          width: '100%',
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          backgroundColor: 'var(--secondary-color)', // Changed to variable for dark mode support
          borderRadius: '16px',
          opacity: actionsOpacity // Bind opacity here
        }}
      >
        <div style={{ display: 'flex', height: '100%' }}>
          {/* Share Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onShare();
              controls.start({ x: 0 });
              setIsOpen(false);
            }}
            style={{
              width: '70px',
              height: '100%',
              backgroundColor: '#4f46e5', // Primary blue
              color: 'white',
              border: 'none',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              padding: 0,
            }}
          >
            <Share size={20} />
            <span style={{ fontSize: '10px', marginTop: '4px', fontWeight: 600 }}>Share</span>
          </button>

          {/* Delete Button - only shown if onDelete is provided */}
          {onDelete && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
                controls.start({ x: 0 });
                setIsOpen(false);
              }}
              style={{
                width: '70px',
                height: '100%',
                backgroundColor: '#ef4444', // Red
                color: 'white',
                border: 'none',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                padding: 0,
              }}
            >
              <Trash2 size={20} />
              <span style={{ fontSize: '10px', marginTop: '4px', fontWeight: 600 }}>Delete</span>
            </button>
          )}
        </div>
      </motion.div>

      {/* Foreground Card */}
      <motion.div
        drag="x"
        dragConstraints={{ left: OPEN_POSITION, right: 0 }}
        dragElastic={0.1}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        animate={controls}
        whileTap={{
          scale: 0.97,
          filter: 'brightness(0.95)',
          transition: { duration: 0.15, ease: "easeOut" }
        }}
        style={{
          x,
          backgroundColor: 'var(--card-background)',
          borderRadius: '16px',
          boxShadow: 'var(--shadow-sm)',
          position: 'relative',
          zIndex: 10,
          height: '100%',
          touchAction: 'pan-y'
        }}
      >
        <div
          ref={contentRef}
          onClick={handleCardClick}
          style={{
            width: '100%',
            height: '100%',
            cursor: 'pointer',
            borderRadius: '16px',
          }}
          onPointerDown={(e) => {
            hasMoved.current = false;
            startX.current = e.clientX;
          }}
          onPointerMove={(e) => {
            // Detect any movement beyond threshold
            const moved = Math.abs(e.clientX - startX.current);
            if (moved > MOVEMENT_THRESHOLD && !hasMoved.current) {
              hasMoved.current = true;
            }
          }}
        >
          {children}
        </div>
      </motion.div>
    </div>
  );
};

export default SwipeableTourCard;

